'use client';
import { getTrenchRadarInfo } from '@/actions/trenchRadar';
import { tokenInfoAtom } from '@/atoms/tokenInfo';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { TooltipProvider } from '@/components/ui/tooltip';
import { LINKS } from '@/constants';
import { PrimaryCategory, TrenchRadarResponse } from '@/types/trenchRadar';
import { formatNumber } from '@/utils/formatText';
import { useDebounce } from '@uidotdev/usehooks';
import * as d3 from 'd3';
import { atom, useAtom, useAtomValue } from 'jotai';
import { useEffect, useRef, useState } from 'react';

interface Node extends d3.SimulationNodeDatum {
  id: string;
  value: number;
  label: string;
  percentage: number;
  category: string;
  wallets: number;
  solSpent: number;
  tokensBought: number;
  tokensHolding: number;
}

interface CategoryInfo {
  label: string;
  color: string;
  borderColor: string;
}

const categories: Record<string, CategoryInfo> = {
  [PrimaryCategory.Sniper]: {
    label: 'Sniper',
    color: 'rgba(239, 68, 68, 0.2)',
    borderColor: '#ef4444',
  },
  [PrimaryCategory.Regular]: {
    label: 'Regular',
    color: 'rgba(168, 85, 247, 0.2)',
    borderColor: '#a855f7',
  },
  team: {
    label: 'Team',
    color: 'rgba(34, 197, 94, 0.2)',
    borderColor: '#22c55e',
  },
  new_wallet: {
    label: 'New Wallet',
    color: 'rgba(59, 130, 246, 0.2)',
    borderColor: '#3b82f6',
  },
  copy_trader: {
    label: 'Copy Trader',
    color: 'rgba(236, 72, 153, 0.2)',
    borderColor: '#ec4899',
  },
};

export const trenchRadarAtom = atom<TrenchRadarResponse | null>(null);

export const TokenMap = () => {
  const [data, setData] = useAtom(trenchRadarAtom);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [width, setWidth] = useState<number>(0);
  const [height, setHeight] = useState<number>(0);
  const debouncedWidth = useDebounce(width, 500);
  const debouncedHeight = useDebounce(height, 500);
  const tokenInfo = useAtomValue(tokenInfoAtom);
  const containerRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    const observer = new ResizeObserver(entries => {
      if (!containerRef.current) return;
      const { clientHeight, clientWidth } = containerRef.current;
      setHeight(clientHeight);
      setWidth(clientWidth);
    });

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, []);

  const totalBundles = Object.entries(data?.bundles || {});
  const nodes: Node[] = totalBundles
    .filter(([_, bundle]) => bundle.holding_percentage > 0)
    .map(([id, bundle]) => ({
      id,
      value: bundle.total_tokens,
      label: `Bundle ${id}`,
      percentage: bundle.holding_percentage,
      category: bundle.bundle_analysis.primary_category,
      wallets: bundle.unique_wallets,
      solSpent: bundle.total_sol,
      tokensBought: bundle.total_tokens / 1000000,
      tokensHolding: bundle.holding_amount / 1000000,
    }));

  useEffect(() => {
    if (!svgRef.current || !data) return;

    const svg = d3.select(svgRef.current);

    svg.selectAll('*').remove();

    const g = svg.append('g');

    const zoom = d3
      .zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.5, 5])
      .on('zoom', event => {
        g.attr('transform', event.transform);
      });

    svg.call(zoom);

    const minRadius = 30;
    const getRadius = (percentage: number) => {
      const calculatedRadius = Math.sqrt(percentage) * 20;
      return Math.max(calculatedRadius, minRadius);
    };

    const simulation = d3
      .forceSimulation<Node>(nodes)
      .force('center', d3.forceCenter(debouncedWidth / 2, debouncedHeight / 2))
      .force('charge', d3.forceManyBody().strength(-30))
      .force(
        'collide',
        d3
          .forceCollide()
          .radius(d => getRadius((d as any).percentage) + 4)
          .strength(0.8),
      )
      .force('x', d3.forceX(debouncedWidth / 2).strength(0.05))
      .force('y', d3.forceY(debouncedHeight / 2).strength(0.05));

    const nodeElements = g
      .selectAll('g')
      .data(nodes)
      .enter()
      .append('g')
      .call(
        d3
          .drag<SVGGElement, Node>()
          .on('start', dragstarted)
          .on('drag', dragged)
          .on('end', dragended),
      );

    const defs = svg.append('defs');
    const filter = defs.append('filter').attr('id', 'drop-shadow').attr('height', '130%');

    filter
      .append('feGaussianBlur')
      .attr('in', 'SourceAlpha')
      .attr('stdDeviation', 2)
      .attr('result', 'blur');

    filter
      .append('feOffset')
      .attr('in', 'blur')
      .attr('dx', 1)
      .attr('dy', 1)
      .attr('result', 'offsetBlur');

    const feMerge = filter.append('feMerge');
    feMerge.append('feMergeNode').attr('in', 'offsetBlur');
    feMerge.append('feMergeNode').attr('in', 'SourceGraphic');

    nodeElements
      .append('circle')
      .attr('r', d => getRadius(d.percentage))
      .attr('fill', d => categories[d.category]?.color || '#f1f5f9')
      .attr('stroke', d => categories[d.category]?.borderColor || '#ef4444')
      .attr('stroke-width', 2)
      .attr('filter', 'url(#drop-shadow)')
      .style('cursor', 'pointer')
      .on('mouseover', function (event, d) {
        d3.select(this).transition().duration(200).attr('stroke-width', 3);
      })
      .on('mouseout', function (event, d) {
        if (selectedNode?.id !== d.id) {
          d3.select(this).transition().duration(200).attr('stroke-width', 2);
        }
      })
      .on('click', function (event, d) {
        if (selectedNode) {
          d3.selectAll('circle')
            .filter(function (node: any) {
              return d.id === selectedNode.id;
            })
            .transition()
            .duration(200)
            .attr('stroke-width', 2);
        }

        d3.select(this).transition().duration(200).attr('stroke-width', 4);

        setSelectedNode(d);
      });

    nodeElements
      .append('text')
      .text(d => `${d.percentage.toFixed(3)}%`)
      .attr('text-anchor', 'middle')
      .attr('dy', '.3em')
      .attr('fill', '#FFFFFF')
      .style('font-size', '12px')
      .style('font-weight', 'bold')
      .style('pointer-events', 'none')
      .style('user-select', 'none');

    simulation.on('tick', () => {
      nodeElements.attr('transform', d => `translate(${d.x},${d.y})`);
    });

    function dragstarted(event: d3.D3DragEvent<SVGGElement, Node, Node>) {
      if (!event.active) simulation.alphaTarget(0.3).restart();
      event.subject.fx = event.subject.x;
      event.subject.fy = event.subject.y;
    }

    function dragged(event: d3.D3DragEvent<SVGGElement, Node, Node>) {
      event.subject.fx = event.x;
      event.subject.fy = event.y;
    }

    function dragended(event: d3.D3DragEvent<SVGGElement, Node, Node>) {
      if (!event.active) simulation.alphaTarget(0);
      event.subject.fx = null;
      event.subject.fy = null;
    }

    return () => {
      simulation.stop();
    };
  }, [data, debouncedWidth, debouncedHeight]);

  const handleReset = () => {
    setSelectedNode(null);
    setData(null);
    setError(false);
  };

  if (!tokenInfo) return null;

  return (
    <Card className="w-full h-fit mx-auto inset-0" ref={cardRef}>
      <CardHeader className="relative">
        <CardTitle>Bundle Analysis</CardTitle>
        <p className="text-sm text-white">Are there bundles in the token? Check here.</p>
      </CardHeader>
      <CardContent className="relative flex flex-col gap-6">
        {data && (data?.total_holding_amount || 0) > 0 && (
          <Card className="flex-1">
            <CardHeader className="p-4">
              <CardTitle>General Info</CardTitle>
            </CardHeader>
            <CardContent className="px-4 grid grid-cols-1 sm:grid-cols-3 text-gray-200 text-center">
              <TooltipProvider>
                <div>
                  <p>
                    <strong>Total Bundles</strong>
                  </p>
                  {nodes.length}
                </div>
                <div>
                  <p>
                    <strong>Total Bundle Percentage</strong>
                  </p>
                  {Number(data?.total_holding_percentage).toFixed(2)}%
                </div>
                <div>
                  <p>
                    <strong>Total Bundle Value</strong>
                  </p>
                  $
                  {formatNumber(
                    Number(((tokenInfo.mc * data?.total_holding_percentage) / 100).toFixed(2)),
                  )}
                </div>
              </TooltipProvider>
            </CardContent>
          </Card>
        )}
        <div className="relative flex flex-col-reverse md:flex-row gap-6 justify-between">
          {tokenInfo.ca.slice(-4) !== 'pump' ? (
            <p className="text-gray-300 font-semibold">TrenchRadar only supports pump.fun tokens</p>
          ) : (
            <>
              {error ? (
                <div>
                  <p className="text-gray-300">
                    There was an error fetching the bundle data. You can{' '}
                    <span
                      className="text-purple-300 hover:text-purple-600 cursor-pointer"
                      onClick={async () => {
                        setError(false);
                        setLoading(true);
                        try {
                          const { data, error } = await getTrenchRadarInfo(tokenInfo?.ca);
                          if (error) return;
                          setData(data);
                        } catch {
                          setError(true);
                        } finally {
                          setLoading(false);
                        }
                      }}
                    >
                      try again
                    </span>
                    , or{' '}
                    <a
                      className="text-purple-300 hover:text-purple-600"
                      href={`https://trench.bot/bundles/${tokenInfo.ca}`}
                      target="_blank"
                    >
                      head to TrenchRadar
                    </a>{' '}
                    if the error occurs again.
                  </p>
                </div>
              ) : !data ? (
                <div className="w-fit mx-auto flex flex-col items-center justify-center gap-3">
                  <Button
                    type="button"
                    loading={loading}
                    dynamicLoader
                    onClick={async () => {
                      setError(false);
                      setLoading(true);
                      try {
                        const { data, error } = await getTrenchRadarInfo(tokenInfo?.ca);
                        if (error) return;
                        setData(data);
                      } catch {
                        setError(true);
                      } finally {
                        setLoading(false);
                      }
                    }}
                    className="w-full"
                  >
                    Click to fetch bubble map information
                  </Button>
                  {loading && (
                    <p className="text-gray-500">
                      This may take a bit, we're grabbing you the latest info 😉
                    </p>
                  )}
                </div>
              ) : data.total_holding_amount < 1 ? (
                <p className="text-gray-300 font-semibold">
                  🎉 All bundles that were holding this coin have sold their tokens.
                </p>
              ) : (
                <div className="max-h-[500px] flex-1 flex flex-col h-fit gap-6">
                  <div ref={containerRef} className="flex-1 w-full min-h-80">
                    <svg ref={svgRef} width="100%" height={debouncedHeight} className="mx-auto" />
                  </div>
                  <Card>
                    <CardHeader className="p-4">
                      <CardTitle>Categories</CardTitle>
                    </CardHeader>
                    <CardContent className="px-4">
                      <div className="space-y-2 grid grid-cols-3">
                        {Array.from(new Set(nodes.map(node => node.category))).map(category => {
                          const { label, color, borderColor } = categories[category];
                          return (
                            <div key={category} className="flex items-center gap-2">
                              <div
                                className="w-4 h-4 rounded-full"
                                style={{
                                  backgroundColor: color,
                                  borderColor: borderColor,
                                  borderWidth: 2,
                                }}
                              />
                              <span className="text-sm text-white">{label}</span>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
              {(data?.total_holding_amount || 0) > 0 && (
                <Card className="md:max-w-[216px] min-w-[216px] flex-1">
                  <CardHeader className="p-4">
                    <CardTitle>Bundle Info</CardTitle>
                  </CardHeader>
                  <CardContent className="px-4 flex justify-center items-center">
                    {selectedNode ? (
                      <div className="text-white flex-1">
                        <table className="w-full text-sm">
                          <tbody>
                            <tr className="border-b border-gray-700">
                              <td className="font-bold py-2 pr-4 text-center">
                                Bundle Percentage:
                              </td>
                              <td className="py-2 text-center">
                                {selectedNode.percentage.toFixed(2)}%
                              </td>
                            </tr>
                            <tr className="border-b border-gray-700">
                              <td className="font-bold py-2 pr-4 text-center">Bundle Value:</td>
                              <td className="py-2 text-center">
                                $
                                {formatNumber(
                                  Number(
                                    ((tokenInfo.mc * selectedNode.percentage) / 100).toFixed(2),
                                  ),
                                )}
                              </td>
                            </tr>
                            <tr className="border-b border-gray-700">
                              <td className="font-bold py-2 pr-4 text-center">
                                Number of Wallets:
                              </td>
                              <td className="py-2 text-center">{selectedNode.wallets}</td>
                            </tr>
                            <tr className="border-b border-gray-700">
                              <td className="font-bold py-2 pr-4 text-center">Tokens Remaining:</td>
                              <td className="py-2 text-center">
                                {formatNumber(selectedNode.tokensHolding)}
                              </td>
                            </tr>
                            <tr className="border-b border-gray-700">
                              <td className="font-bold py-2 pr-4 text-center">
                                Total Tokens Bought:
                              </td>
                              <td className="py-2 text-center">
                                {formatNumber(selectedNode.tokensBought)}
                              </td>
                            </tr>
                            <tr>
                              <td className="font-bold py-2 pr-4 text-center">SOL Spent:</td>
                              <td className="py-2 text-center">
                                {selectedNode.solSpent.toFixed(2)}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="h-full flex flex-row gap-5 justify-center items-center text-center text-white flex-1 text-wrap w-full md:w-3/4 mx-auto md:mt-12">
                        <p>Select a bundle to learn more about it</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      </CardContent>
      <CardFooter className="flex justify-between w-full">
        {data && (
          <Button type="button" onClick={handleReset} className="mr-4" variant={'outline'}>
            Reset
          </Button>
        )}
        <p className="text-right text-muted-foreground text-sm ml-auto">
          powered by{' '}
          <a className="text-white hover:text-purple-400" href={LINKS.trenchRadar} target="_blank">
            TrenchRadar
          </a>
        </p>
      </CardFooter>
    </Card>
  );
};
